/**
 * LES LETTRES DE VOITURE (eCMR) — lot 161, 04/11/2026.
 *
 * Les types des routes `/ordres-transport/:id/lettres-voiture…`
 * (`apps/api/src/lettres_voiture.ts`) et le REMPLISSAGE DU MODÈLE FIGÉ
 * (`lettreVoitureGabarit.ts`) à partir du contenu figé à la génération.
 *
 * TOUT CE QUI ENTRE DANS LE MODÈLE EST ÉCHAPPÉ : le contenu vient de saisies
 * (noms de sites, références, instructions) et finit en HTML.
 */

import { GABARIT_LETTRE } from './lettreVoitureGabarit.js';
import { AUTRE_DIMENSION } from './ot.js';

export interface LieuDeLettre {
  readonly nom: string | null;
  readonly adresse: string | null;
  readonly code_postal: string | null;
  readonly ville: string | null;
  readonly pays: string | null;
  readonly reference: string | null;
  readonly jour: string | null;
  readonly debut: string | null;
  readonly fin: string | null;
  readonly instructions: string | null;
}

export interface LigneDeLettre {
  readonly support: string | null;
  readonly quantite: string | null;
  readonly poids: number | null;
  readonly longueur_cm: number | null;
  readonly largeur_cm: number | null;
  readonly hauteur_cm: number | null;
  /**
   * Palettes consignées (échange prévu). ABSENT des lettres générées avant
   * le 04/11 à 15 h 42 : lu comme `false`.
   */
  readonly consigne?: boolean;
}

export interface ContenuDeLettre {
  readonly ot: string;
  readonly categorie: string | null;
  readonly reference_client: string | null;
  readonly donneur: {
    readonly nom: string;
    readonly adresse: string | null;
    readonly code_postal: string | null;
    readonly ville: string | null;
    readonly pays: string | null;
    readonly tva: string | null;
    readonly siret: string | null;
  } | null;
  readonly expediteur: LieuDeLettre;
  readonly destinataire: LieuDeLettre;
  readonly lignes: readonly LigneDeLettre[];
}

export interface LettreVoiture {
  readonly id: string;
  readonly numero: string;
  readonly etape_chargement_id: string;
  readonly etape_livraison_id: string;
  readonly contenu: ContenuDeLettre;
  readonly generee_le: string;
  readonly generee_par: string;
}

/**
 * CE QUE LE CHAUFFEUR A FAIT À UN ARRÊT — lots 163 et 164. Même forme côté
 * exploitation (`GET /ordres-transport/:id/execution`), chauffeur
 * (`/mission/:code`) et client (`/suivi/:code`).
 */
export interface ExecutionArret {
  readonly arrive_le: string | null;
  readonly arrive_lat?: number | string | null;
  readonly arrive_lng?: number | string | null;
  readonly parti_le: string | null;
  readonly signe_le: string | null;
  readonly signe_lat: number | string | null;
  readonly signe_lng: number | string | null;
  readonly signataire: string | null;
  readonly signature_client: string | null;
  readonly signature_chauffeur: string | null;
  readonly reserve_chauffeur: string | null;
  readonly reserve_client: string | null;
  readonly nature: string | null;
  readonly lignes: readonly { readonly etape_livraison_id: string; readonly lignes: readonly LigneDeLettre[] }[] | null;
  readonly livrees: readonly { readonly etape_chargement_id: string; readonly quantites: readonly number[] }[] | null;
}

/** Ce qui remplit les cases du chauffeur d'UNE lettre. */
export interface ExecutionDeLettre {
  /** Nom du chauffeur : case 11 et sous les signatures du transporteur. Absent pour le client. */
  readonly chauffeur: string | null;
  readonly plaques: { readonly tracteur: string; readonly remorque: string | null } | null;
  readonly chargement: ExecutionArret | null;
  readonly livraison: ExecutionArret | null;
}

export interface EtatDesLettres {
  readonly lettres: readonly LettreVoiture[];
  /** Couples chargement → livraison du Packaging qui n'ont pas encore de lettre. */
  readonly couples_sans_lettre: number;
  /** Couples en tout ; 0 = pas de Packaging, rien à générer. */
  readonly couples: number;
}

/** Le modèle a 14 lignes de marchandise (planche du 04/11). */
export const LIGNES_DU_MODELE = 14;

export function echapper(t: string): string {
  return t
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** « 16.00 » → « 16 », « 2.50 » → « 2,5 ». */
export function quantiteDite(q: string | null): string {
  if (q === null || q.trim() === '') return '';
  const n = Number(q);
  return Number.isFinite(n) ? n.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : q;
}

/** 10400 → « 10 400 kg ». */
export function poidsDit(kg: number | null): string {
  return kg === null ? '' : `${kg.toLocaleString('fr-FR')} kg`;
}

/** La date de génération, au jour de Paris : « 04/11/2026 ». */
export function dateDite(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(iso));
}

/** « 45400 Fleury-les-Aubrais FR ». */
function villeDite(l: { code_postal: string | null; ville: string | null; pays: string | null }): string {
  return [l.code_postal, l.ville, l.pays].filter((x) => x !== null && x.trim() !== '').join(' ');
}

/**
 * Les dimensions d'une ligne « Autre dimension » : « 150×150×120 cm ». Celles
 * d'un support de la liste se lisent dans son nom (« Palette 80x120 ») ; les
 * répéter chargerait le tableau sans rien dire de plus.
 */
function dimensionsDites(l: LigneDeLettre): string {
  if (l.support !== AUTRE_DIMENSION || l.longueur_cm === null || l.largeur_cm === null) return '';
  return `${String(l.longueur_cm)}×${String(l.largeur_cm)}${l.hauteur_cm === null ? '' : `×${String(l.hauteur_cm)}`} cm`;
}

/** Une case de texte du modèle, à la position relevée dans le PDF GedMouv. */
function cellule(gauche: number, haut: number, texte: string): string {
  return `<div style="position:absolute;left:${String(gauche)}pt;top:${String(haut)}pt;font-size:7pt;line-height:1.15;color:#000;white-space:nowrap;">${echapper(texte)}</div>`;
}

/**
 * LES LIGNES DE MARCHANDISE : une ligne du Packaging = une ligne du tableau
 * (décision du 04/11), quantité et support dans la colonne 7, poids — et
 * dimensions d'une « Autre dimension » — dans la colonne 9. Bandes de 18 pt
 * à partir de 177,5 pt, comme sur la planche.
 */
export function lignesDuModele(lignes: readonly LigneDeLettre[], nature: string | null = null): string {
  return lignes
    .slice(0, LIGNES_DU_MODELE)
    .map((l, i) => {
      const haut = 177.5 + 18 * i;
      const dims = dimensionsDites(l);
      return (
        cellule(34.5, haut, [quantiteDite(l.quantite), l.support ?? ''].filter((x) => x !== '').join(' ')) +
        // LA NATURE (case 8), saisie par le chauffeur au chargement (lot 164).
        (nature === null ? '' : cellule(192.8, haut, nature)) +
        cellule(440.3, haut, [poidsDit(l.poids), dims].filter((x) => x !== '').join(' · '))
      );
    })
    .join('');
}

/** « 05/11/2026 à 08:12 », à l'heure de Paris. */
export function momentDit(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  const jour = dateDite(iso);
  const heure = new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }).format(d);
  return `${jour} à ${heure}`;
}

/** Sous une signature : « 05/11/2026 à 08:47 · 49.144531, 0.226511 ». */
function lieuDeSignature(x: ExecutionArret): string {
  const gps = x.signe_lat === null || x.signe_lng === null ? '' : `${String(x.signe_lat)}, ${String(x.signe_lng)}`;
  return [momentDit(x.signe_le), gps].filter((t) => t !== '').join(' · ');
}

/** L'image d'une signature. La base n'accepte qu'un PNG en base64 : rien à échapper qu'un guillemet. */
function imageDeSignature(dataUrl: string | null): string {
  if (dataUrl === null || !/^data:image\/png;base64,[A-Za-z0-9+/]+=*$/.test(dataUrl)) return '';
  return `<img alt="" src="${dataUrl}" style="width:100%;height:100%;object-fit:contain;">`;
}

/**
 * LA RÉSERVE D'UN ARRÊT, comme dans la maquette : « Chauffeur : … —
 * Expéditeur : … » ; à la livraison, chaque ligne livrée en moins
 * (« Palette 80x120 : 15 sur 16 »).
 */
export function reserveDite(
  x: ExecutionArret | null,
  qui: 'Expéditeur' | 'Destinataire',
  manquants: readonly string[] = [],
): string {
  // Ce que porte l'exécution, signée OU EN COURS DE SAISIE (l'aperçu de
  // l'étape 4 « Contrôle de la lettre ») : la base n'enregistre les réserves
  // qu'à la signature.
  if (x === null) return '';
  const r: string[] = [];
  if (x.reserve_chauffeur !== null) r.push(`Chauffeur : ${x.reserve_chauffeur}`);
  if (x.reserve_client !== null) r.push(`${qui} : ${x.reserve_client}`);
  return [...r, ...manquants].join(' — ');
}

/** Les lignes CHARGÉES de cette lettre si le chargement est signé, sinon celles figées à la génération. */
export function lignesChargees(l: Pick<LettreVoiture, 'etape_livraison_id' | 'contenu'>, ex: ExecutionDeLettre | null): readonly LigneDeLettre[] {
  const signees = ex?.chargement?.lignes?.find((x) => x.etape_livraison_id === l.etape_livraison_id);
  return signees?.lignes ?? l.contenu.lignes;
}

/** Les quantités livrées de cette lettre, si la livraison est signée. */
export function quantitesLivrees(l: Pick<LettreVoiture, 'etape_chargement_id'>, ex: ExecutionDeLettre | null): readonly number[] | null {
  return ex?.livraison?.livrees?.find((x) => x.etape_chargement_id === l.etape_chargement_id)?.quantites ?? null;
}

/**
 * LE MODÈLE REMPLI, en HTML. Sans exécution, les cases du chauffeur (plaques,
 * heures, signatures, réserves) restent vides ; avec, elles se remplissent de
 * ce qu'il a fait (lots 163 et 164), et les lignes deviennent celles qu'il a
 * signées au chargement.
 */
export function remplirLettre(l: LettreVoiture, ex: ExecutionDeLettre | null = null): string {
  const c = l.contenu;
  const d = c.donneur;
  const ch = ex?.chargement ?? null;
  const li = ex?.livraison ?? null;
  const chSigne = ch !== null && ch.signe_le !== null;
  const liSigne = li !== null && li.signe_le !== null;
  const lignes = lignesChargees(l, ex);
  const livrees = quantitesLivrees(l, ex);
  const totalQuantite = lignes.reduce((s, x) => s + (Number(x.quantite) || 0), 0);
  const totalPoids = lignes.reduce((s, x) => s + (x.poids ?? 0), 0);
  // LES PALETTES CHARGÉES NE COMPTENT QUE LES LIGNES CONSIGNÉES (04/11,
  // 15 h 42) : les cases palettes servent à l'échange, rien d'autre.
  const consignees = lignes
    .filter((x) => x.consigne === true)
    .reduce((s, x) => s + (Number(x.quantite) || 0), 0);
  const manquants =
    livrees === null
      ? []
      : lignes.flatMap((x, i) => {
          const q = livrees[i];
          return q === undefined || q === Number(x.quantite)
            ? []
            : [`${x.support ?? ''} : ${quantiteDite(String(q))} sur ${quantiteDite(x.quantite)}`];
        });
  const valeurs: Record<string, string> = {
    NUM: l.numero,
    DATE: dateDite(l.generee_le),
    OT: c.ot,
    DO1: d?.nom ?? '',
    DO2: d?.adresse ?? '',
    DO3: d === null ? '' : villeDite(d),
    REFDO: c.reference_client ?? '',
    DOTVA: d?.tva ? `TVA : ${d.tva}` : '',
    DOSIRET: d?.siret ? `Siret : ${d.siret}` : '',
    E1: c.expediteur.nom ?? '',
    E2: c.expediteur.adresse ?? '',
    E3: villeDite(c.expediteur),
    C1: c.expediteur.nom ?? '',
    C2: c.expediteur.adresse ?? '',
    C3: villeDite(c.expediteur),
    D1: c.destinataire.nom ?? '',
    D2: c.destinataire.adresse ?? '',
    D3: villeDite(c.destinataire),
    REFC: c.expediteur.reference ?? '',
    REFL: c.destinataire.reference ?? '',
    // LES INSTRUCTIONS NE SE REMPLISSENT JAMAIS (Mohamed, 04/11 15 h 42) :
    // la case est là pour la forme.
    INSTR: '',
    PALC: consignees === 0 ? '' : quantiteDite(String(consignees)),
    P: poidsDit(totalPoids),
    Q2: quantiteDite(String(totalQuantite)),
    // LES CASES DU CHAUFFEUR — lots 163 et 164.
    COND: ex?.chauffeur ?? '',
    IMT: ex?.plaques?.tracteur ?? '',
    IMR: ex?.plaques === null || ex?.plaques === undefined ? '' : (ex.plaques.remorque ?? '-'),
    ARRC: momentDit(ch?.arrive_le ?? null),
    DEPC: momentDit(ch?.parti_le ?? null),
    ARRL: momentDit(li?.arrive_le ?? null),
    DEPL: momentDit(li?.parti_le ?? null),
    RES12: reserveDite(ch, 'Expéditeur'),
    RES13: reserveDite(li, 'Destinataire', manquants),
    N14: chSigne ? (ch.signataire ?? '') : '',
    N15: chSigne ? (ex?.chauffeur ?? '') : '',
    N16: liSigne ? (ex?.chauffeur ?? '') : '',
    N17: liSigne ? (li.signataire ?? '') : '',
    G14: chSigne ? lieuDeSignature(ch) : '',
    G15: chSigne ? lieuDeSignature(ch) : '',
    G16: liSigne ? lieuDeSignature(li) : '',
    G17: liSigne ? lieuDeSignature(li) : '',
  };
  // Les jetons dont la valeur est du HTML fabriqué ici, jamais une saisie.
  const html: Record<string, string> = {
    LIGNES: lignesDuModele(lignes, ch?.nature ?? null),
    S14: chSigne ? 'on' : 'off',
    S15: chSigne ? 'on' : 'off',
    S16: liSigne ? 'on' : 'off',
    S17: liSigne ? 'on' : 'off',
    SI14: chSigne ? imageDeSignature(ch.signature_client) : '',
    SI15: chSigne ? imageDeSignature(ch.signature_chauffeur) : '',
    SI16: liSigne ? imageDeSignature(li.signature_chauffeur) : '',
    SI17: liSigne ? imageDeSignature(li.signature_client) : '',
  };
  return GABARIT_LETTRE.replace(/%([A-Z0-9]+)%/g, (_m, cle: string) => {
    if (cle in html) return html[cle] ?? '';
    const v = valeurs[cle];
    return v === undefined ? '' : echapper(v);
  });
}

/**
 * LE PDF D'UNE LETTRE (lots 164 et 166) : la lettre seule, à la taille A4, dans
 * une page à part qui s'imprime d'elle-même — « Enregistrer au format PDF » du
 * navigateur. Même page pour le client et pour l'onglet Documents.
 */
export function pageImprimableDeLettre(l: LettreVoiture, ex: ExecutionDeLettre | null): string {
  return `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Lettre de voiture ${echapper(l.numero)}</title>
<style>@page{size:A4;margin:0}html,body{margin:0;background:#fff}.sig-off{visibility:hidden}</style></head>
<body>${remplirLettre(l, ex)}
<script>window.addEventListener('load',function(){setTimeout(function(){window.print()},300)})</script></body></html>`;
}

/** Ouvre le PDF dans un nouvel onglet. */
export function ouvrirLePdfDeLettre(l: LettreVoiture, ex: ExecutionDeLettre | null): void {
  const url = URL.createObjectURL(new Blob([pageImprimableDeLettre(l, ex)], { type: 'text/html' }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
